/**
 * Chest Burst — what happens when a child taps the treasure chest and finds
 * new coins inside: the lid flies open, coins arc out, sparkles shower, and
 * the new coins count up. Plays once per batch of fresh coins.
 */
import { useEffect, useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import confetti from 'canvas-confetti';

const CHEST_SRC = 'https://static.prod-images.emergentagent.com/jobs/eb175c5d-51a0-4ace-b433-822f579519a6/images/f894c6f567b856285bbb9287e4bb5ee0d383f342edddb76a718fbf2e5b82c08c.jpeg';

function Coin({ delay, x, spin }) {
  return (
    <motion.span
      className="absolute left-1/2 top-1/2 w-7 h-7 rounded-full bg-gradient-to-br from-yellow-200 via-amber-400 to-orange-500 ring-2 ring-amber-200/80 shadow-lg"
      initial={{ x: -14, y: -14, opacity: 0, scale: 0.5 }}
      animate={{
        x: [-14, x * 0.65, x],
        y: [-14, -170, 120],
        opacity: [0, 1, 1, 0],
        scale: [0.5, 1, 0.9],
        rotate: spin,
      }}
      transition={{ duration: 1.9, delay, ease: 'easeOut', times: [0, 0.45, 1] }}
    >
      <span className="absolute inset-[5px] rounded-full border border-amber-700/35" />
    </motion.span>
  );
}

export function ChestBurst({ open, coins = 0, onClose }) {
  const [shown, setShown] = useState(0);

  const sparks = useMemo(
    () => Array.from({ length: 14 }, (_, i) => ({
      delay: 0.25 + i * 0.055,
      x: (i % 2 ? 1 : -1) * (40 + ((i * 37) % 170)),
      spin: (i % 2 ? 1 : -1) * (180 + ((i * 53) % 360)),
    })),
    [],
  );

  useEffect(() => {
    if (!open) { setShown(0); return undefined; }
    confetti({
      particleCount: 110, spread: 100, startVelocity: 42, gravity: 0.9, ticks: 200,
      origin: { x: 0.5, y: 0.55 },
      colors: ['#FCD34D', '#F59E0B', '#FDE68A', '#FB7185', '#FFFFFF'],
      disableForReducedMotion: true,
    });
    const target = Number(coins) || 0;
    if (target <= 0) return undefined;
    const steps = Math.min(32, target);
    const inc = Math.ceil(target / steps);
    let n = 0;
    const id = setInterval(() => {
      n = Math.min(target, n + inc);
      setShown(n);
      if (n >= target) clearInterval(id);
    }, 45);
    return () => clearInterval(id);
  }, [open, coins]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[80] flex items-center justify-center bg-slate-950/70 backdrop-blur-sm px-6"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          onClick={onClose}
          data-testid="chest-burst">
          <motion.div
            className="relative flex flex-col items-center"
            initial={{ scale: 0.8, y: 20 }} animate={{ scale: 1, y: 0 }}
            transition={{ type: 'spring', stiffness: 180, damping: 16 }}>

            {/* Glow behind the chest */}
            <motion.span
              className="absolute w-64 h-64 rounded-full bg-amber-400/35 blur-3xl"
              initial={{ scale: 0.4, opacity: 0 }}
              animate={{ scale: [0.4, 1.25, 1], opacity: [0, 0.95, 0.6] }}
              transition={{ duration: 1.5 }} />

            <div className="relative w-40 h-40 flex items-center justify-center">
              {sparks.map(s => <Coin key={s.delay} {...s} />)}
              <motion.img
                src={CHEST_SRC} alt=""
                className="relative w-32 h-32 rounded-2xl object-cover ring-4 ring-amber-200/70 shadow-2xl"
                initial={{ rotate: -4 }}
                animate={{ rotate: [-4, 5, -3, 2, 0], scale: [1, 1.12, 1.04] }}
                transition={{ duration: 1.1 }} />
            </div>

            <motion.p
              className="mt-6 font-heading text-5xl font-extrabold text-amber-200 drop-shadow-[0_3px_14px_rgba(0,0,0,0.6)]"
              initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
              data-testid="chest-burst-count">
              +{shown.toLocaleString('en-IN')}
            </motion.p>
            <p className="font-heading text-sm text-white/90 mt-1">Veda Coins, all yours</p>
            <p className="font-body text-[11px] text-white/55 mt-4">Tap anywhere to close</p>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default ChestBurst;
